import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useChild } from '../contexts/ChildContext';
import { useAuth } from '../contexts/AuthContext';
import { useTimer } from '../contexts/TimerContext';
import { useRecentItems } from '../hooks/useRecentItems';
import { useDashboardSettings } from '../hooks/useDashboardSettings';
import { useActivityFeed } from '../hooks/useActivityFeed';
import { parseItemDate, formatTimeAgo } from '../utils/dateUtils';
import DailySummary from '../components/summary/dailySummary';
import InsightCards from '../components/insights/InsightCards';
import WakeWindowCard from '../components/dashboard/WakeWindowCard';
import NapReminderBanner from '../components/dashboard/NapReminderBanner';
import LastLoggedStrip from '../components/dashboard/LastLoggedStrip';
import TrendCharts from '../components/charts/TrendCharts';
import ActivityFeed from '../components/ui/ActivityFeed';
import DashboardCustomizer from '../components/ui/DashboardCustomizer';
import { exportFullReportPDF } from '../utils/pdfUtils';
import '../styles/activityFeed.css';
import {
  bottleCSVColumns, diaperCSVColumns, sleepCSVColumns, waterCSVColumns,
  solidFoodCSVColumns, foodIntroCSVColumns, growthHealthCSVColumns,
  milestoneCSVColumns, doctorVisitCSVColumns, vaccinationCSVColumns,
  allergyCSVColumns, breastfeedingCSVColumns,
} from '../utils/exportUtils';

/**
 * DashboardPage — overview/summary page at /app.
 * Shows today's stats, smart insights, and trend charts.
 * Does NOT render all section forms/tables — each section has its own route.
 *
 * Uses useRecentItems (one-time getDocs queries) instead of useFirestore
 * (persistent onSnapshot listeners) to reduce Firestore read count on initial load.
 * Each collection fetches only the last 5 items within the past 48 hours.
 */
export default function DashboardPage() {
  const { user } = useAuth();
  const { selectedChild } = useChild();
  const { timer, elapsed } = useTimer();
  const sleepTimerRunning = timer?.type === 'sleep';
  const { sections, saveSettings } = useDashboardSettings(user?.uid);
  const showWakeWindow = sections.find((s) => s.id === 'wakeWindow')?.visible !== false;
  const [customizerOpen, setCustomizerOpen] = useState(false);

  // One-time queries — no persistent Firestore listeners on the overview page.
  // hoursBack=48 gives a wider window to capture "today's" data even if the user
  // opened the app just after midnight; limit=5 keeps read counts low.
  const { items: bottleData,        loading: loadingBottles }       = useRecentItems('bottleFeeds',       selectedChild?.id, 5,  48);
  const { items: solidFoodData,     loading: loadingSolidFoods }    = useRecentItems('solidFoods',        selectedChild?.id, 5,  48);
  const { items: waterData,         loading: loadingWater }         = useRecentItems('waterIntake',       selectedChild?.id, 5,  48);
  const { items: foodIntroData,     loading: loadingFoodIntros }    = useRecentItems('foodIntroductions', selectedChild?.id, 5,  48);
  const { items: sleepData,         loading: loadingSleep }         = useRecentItems('sleepTracking',     selectedChild?.id, 5,  48);
  const { items: extendedSleepData, loading: loadingExtendedSleep } = useRecentItems('sleepTracking',     selectedChild?.id, 30, 336);
  const { items: growthHealthData,  loading: loadingGrowthHealth }  = useRecentItems('growthHealth',      selectedChild?.id, 5,  48);
  const { items: milestoneData,     loading: loadingMilestones }    = useRecentItems('milestones',        selectedChild?.id, 5,  48);
  const { items: diaperData,        loading: loadingDiapers }       = useRecentItems('diapers',           selectedChild?.id, 5,  48);
  const { items: allergyData,       loading: loadingAllergies }     = useRecentItems('allergies',         selectedChild?.id, 5,  48);
  const { items: vaccinationData,   loading: loadingVaccinations }  = useRecentItems('vaccinations',      selectedChild?.id, 5,  48);
  const { items: doctorVisitData,   loading: loadingDoctorVisits }  = useRecentItems('doctorVisits',      selectedChild?.id, 5,  48);
  const { items: breastfeedingData, loading: loadingBreastfeeding } = useRecentItems('breastfeeding',     selectedChild?.id, 5,  48);

  // Trend-specific queries: wider window (1 year) for the TrendCharts component.
  // These do NOT block the dashboard loading gate — TrendCharts handles its own
  // empty state gracefully while these load in the background.
  const { items: trendBottleData }  = useRecentItems('bottleFeeds',       selectedChild?.id, 2000, 8760);
  const { items: trendWaterData }   = useRecentItems('waterIntake',        selectedChild?.id, 2000, 8760);
  const { items: trendSleepData }   = useRecentItems('sleepTracking',      selectedChild?.id, 2000, 8760);
  const { items: trendSolidData }   = useRecentItems('solidFoods',         selectedChild?.id, 2000, 8760);
  const { items: trendGrowthData }  = useRecentItems('growthHealth',       selectedChild?.id, 2000, 8760);

  const { items: activityItems, loading: loadingActivity } = useActivityFeed(selectedChild?.id, 100);

  // Gate the whole page — render nothing until every query has resolved.
  // This prevents components from rendering in one by one with flashing skeletons.
  const isDashboardLoading = [
    loadingBottles, loadingSolidFoods, loadingWater, loadingFoodIntros,
    loadingSleep, loadingExtendedSleep, loadingGrowthHealth, loadingMilestones,
    loadingDiapers, loadingAllergies, loadingVaccinations, loadingDoctorVisits,
    loadingBreastfeeding, loadingActivity,
  ].some(Boolean);

  const handleFullReportPDF = () => exportFullReportPDF(
    [
      { title: 'Bottle Feeds',       data: bottleData,       columns: bottleCSVColumns },
      { title: 'Nappy Changes',       data: diaperData,       columns: diaperCSVColumns },
      { title: 'Sleep Tracking',     data: sleepData,        columns: sleepCSVColumns },
      { title: 'Water Intake',       data: waterData,        columns: waterCSVColumns },
      { title: 'Solid Foods',        data: solidFoodData,    columns: solidFoodCSVColumns },
      { title: 'Food Introductions', data: foodIntroData,    columns: foodIntroCSVColumns },
      { title: 'Growth & Health',    data: growthHealthData, columns: growthHealthCSVColumns },
      { title: 'Milestones',         data: milestoneData,    columns: milestoneCSVColumns },
      { title: 'Doctor Visits',      data: doctorVisitData,  columns: doctorVisitCSVColumns },
      { title: 'Vaccinations',       data: vaccinationData,  columns: vaccinationCSVColumns },
      { title: 'Food Allergies',     data: allergyData,      columns: allergyCSVColumns },
      { title: 'Breastfeeding',      data: breastfeedingData,columns: breastfeedingCSVColumns },
    ],
    selectedChild?.name || 'Child',
    selectedChild?.dateOfBirth || ''
  );

  // Today's quick stats + last-logged times
  const todayStats = useMemo(() => {
    const today = new Date();
    const isToday = (item) => {
      const d = parseItemDate(item);
      return d && d.toDateString() === today.toDateString();
    };

    // Today totals
    const todayBottles = bottleData.filter(isToday);
    const totalOz = todayBottles.reduce((s, i) => s + parseFloat(i.amount || 0), 0);
    const todaySleep = sleepData.filter(isToday);
    const sleepMinutes = todaySleep.reduce((total, item) => {
      if (item.startTime && item.endTime) {
        const start = new Date(`2000-01-01T${item.startTime}`);
        const end   = new Date(`2000-01-01T${item.endTime}`);
        const raw   = end - start;
        const diff  = raw > 0 ? raw : raw + 24 * 60 * 60 * 1000;
        return total + Math.floor(diff / 60000);
      }
      return total;
    }, 0);
    const sleepH = Math.floor(sleepMinutes / 60);
    const sleepM = sleepMinutes % 60;
    const todayDiapers = diaperData.filter(isToday);
    const bfToday = breastfeedingData.filter(isToday);
    const bfMins  = bfToday.reduce((s, i) => s + parseFloat(i.duration || 0), 0);

    // Last-logged times — data is already sorted newest-first by useRecentItems.
    // For sleep, prefer the end time of the most recent completed session (when
    // they woke up) so the "last sleep" reflects when they were last awake, not
    // when they went down.
    const lastBottle = bottleData[0] ? parseItemDate(bottleData[0]) : null;
    const lastDiaper = diaperData[0] ? parseItemDate(diaperData[0]) : null;
    const lastBf     = breastfeedingData[0] ? parseItemDate(breastfeedingData[0]) : null;
    const lastSleep  = (() => {
      const s = sleepData[0];
      if (!s) return null;
      if (s.endTime) {
        const base = parseItemDate(s);
        if (base) {
          const dt = new Date(`${base.toISOString().split('T')[0]}T${s.endTime}`);
          if (!isNaN(dt.getTime())) return dt;
        }
      }
      return parseItemDate(s);
    })();

    return {
      bottles:       { today: todayBottles.length > 0 ? `${todayBottles.length} feeds · ${totalOz.toFixed(1)} oz` : null, last: formatTimeAgo(lastBottle), hasAny: bottleData.length > 0 },
      sleep:         { today: sleepMinutes        > 0 ? `${sleepH}h ${sleepM}m today` : null,                             last: formatTimeAgo(lastSleep),  hasAny: sleepData.length > 0 },
      diapers:       { today: todayDiapers.length > 0 ? `${todayDiapers.length} today` : null,                            last: formatTimeAgo(lastDiaper), hasAny: diaperData.length > 0 },
      breastfeeding: { today: bfToday.length      > 0 ? `${bfToday.length} sessions · ${bfMins} min` : null,             last: formatTimeAgo(lastBf),     hasAny: breastfeedingData.length > 0 },
    };
  }, [bottleData, sleepData, diaperData, breastfeedingData]);

  // Fixed overlay: covers the layout shell (sidebar, top bar) so the user sees one
  // continuous loading experience rather than the shell appearing mid-load.
  if (isDashboardLoading) {
    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--bg-white)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        gap: '1rem',
      }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
        <p style={{ margin: 0, color: 'var(--text-light)', fontSize: '0.9rem' }}>
          Loading dashboard…
        </p>
      </div>
    );
  }

  if (!selectedChild) {
    return (
      <div className="section-page">
        <div className="dashboard-empty-state">
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>👶</div>
          <h4>No child selected</h4>
          <p style={{ color: 'var(--text-light)' }}>
            Tap the child switcher in the top bar to add or select a child.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="section-page" data-testid="dashboard-page">
      {/* Page header */}
      <div className="dashboard-header" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div>
            <h1 className="page-header-title">👶 {selectedChild.name}'s Dashboard</h1>
            <p className="page-header-subtitle">Today's overview and tracking summary</p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              data-tutorial="customise"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => setCustomizerOpen(true)}
              style={{ borderRadius: '50px' }}
              data-testid="customise-btn"
            >
              ⚙ Customise
            </button>
            <button
              className="btn btn-primary btn-sm"
              onClick={handleFullReportPDF}
              style={{ borderRadius: '50px' }}
              data-testid="full-report-btn"
            >
              ⬇ Full Report PDF
            </button>
          </div>
        </div>
      </div>

      {/* Last logged strip — compact per-section recency grid, ticks every 60s */}
      <LastLoggedStrip
        childId={selectedChild?.id}
        dateOfBirth={selectedChild?.dateOfBirth}
      />

      {/* Quick stats row — visible when any data exists in the last 48h, or sleep timer is running */}
      {(todayStats.bottles.hasAny || todayStats.sleep.hasAny || todayStats.diapers.hasAny || todayStats.breastfeeding.hasAny || sleepTimerRunning) && (
        <div className="row g-2 mb-3" data-testid="quick-stats">
          {[
            { stat: todayStats.bottles,       icon: '🍼', label: 'Bottle Feeds',  to: '/app/bottle-feeds'  },
            { stat: todayStats.breastfeeding, icon: '🤱', label: 'Breastfeeding', to: '/app/breast-feeding' },
            {
              stat: sleepTimerRunning
                ? { today: 'Sleeping now', last: `${Math.floor(elapsed / 60)}m elapsed`, hasAny: true }
                : todayStats.sleep,
              icon: '😴', label: 'Sleep', to: '/app/sleep',
            },
            { stat: todayStats.diapers,       icon: '🧷', label: 'Nappies',       to: '/app/diapers'       },
          ].filter((c) => c.stat.hasAny).map(({ stat, icon, label, to }) => (
            <div key={label} className="col-6 col-md-3">
              <Link to={to} className="card text-decoration-none h-100">
                <div className="card-body text-center py-2">
                  <div style={{ fontSize: '1.5rem' }}>{icon}</div>
                  <small className="fw-semibold d-block" style={{ color: 'var(--primary-dark)' }}>
                    {stat.today ?? 'None today'}
                  </small>
                  {stat.last && (
                    <small style={{ color: 'var(--text-light)', fontSize: '0.72rem' }}>
                      {sleepTimerRunning && label === 'Sleep' ? stat.last : `Last: ${stat.last}`}
                    </small>
                  )}
                </div>
              </Link>
            </div>
          ))}
        </div>
      )}

      {/* Nap reminder — shown when wake window section is visible and no nap logged */}
      {showWakeWindow && (
        <NapReminderBanner
          dateOfBirth={selectedChild?.dateOfBirth}
          sleepData={sleepData}
        />
      )}

      {/* Wake Window Card */}
      {showWakeWindow && (
        <div className="mb-3">
          <WakeWindowCard
            dateOfBirth={selectedChild?.dateOfBirth}
            sleepData={sleepData}
            extendedSleepData={extendedSleepData}
          />
        </div>
      )}

      {/* Daily Summary */}
      <div className="mb-4" data-tutorial="summary-section">
        <h3 style={{ color: 'var(--primary-dark)', fontWeight: '700', marginBottom: '0.75rem' }}>📊 Today's Summary</h3>
        <DailySummary
          bottleData={bottleData}
          solidFoodData={solidFoodData}
          waterData={waterData}
          sleepData={sleepData}
          milestoneData={milestoneData}
          diaperData={diaperData}
          allergyData={allergyData}
          breastfeedingData={breastfeedingData}
        />
      </div>

      <div className="section-divider" />

      {/* Smart Insights */}
      <div className="mb-4">
        <h3 style={{ color: 'var(--primary-dark)', fontWeight: '700', marginBottom: '0.85rem' }}>💡 Smart Insights</h3>
        <InsightCards childId={selectedChild?.id} />
      </div>

      <div className="section-divider" />

      {/* Trend Charts */}
      <div className="mb-4">
        <h3 style={{ color: 'var(--primary-dark)', fontWeight: '700', marginBottom: '1.5rem' }}>📈 Trends</h3>
        <TrendCharts
          bottleData={trendBottleData}
          waterData={trendWaterData}
          sleepData={trendSleepData}
          solidFoodData={trendSolidData}
          growthHealthData={trendGrowthData}
        />
      </div>

      <div className="section-divider" />

      {/* Activity Feed */}
      <div className="mb-4">
        <h3 style={{ color: 'var(--primary-dark)', fontWeight: '700', marginBottom: '1rem' }}>👥 Activity Feed</h3>
        <div className="card">
          <div className="card-body" style={{ padding: '0.5rem 1rem' }}>
            <ActivityFeed items={activityItems} loading={loadingActivity} />
          </div>
        </div>
      </div>

      {/* Dashboard Customizer modal */}
      <DashboardCustomizer
        isOpen={customizerOpen}
        sections={sections}
        onSave={saveSettings}
        onClose={() => setCustomizerOpen(false)}
      />
    </div>
  );
}
